import React from 'react';
import {
  Button,
  CalendarCell,
  CalendarGrid,
  CalendarGridBody,
  CalendarGridHeader,
  CalendarHeaderCell,
  DateInput,
  DateRangePicker as AriaDateRangePicker,
  DateSegment,
  Dialog,
  FieldError,
  Group,
  Heading,
  Label,
  Popover,
  RangeCalendar,
  Text,
} from 'react-aria-components';
import type {
  DateRangePickerProps as AriaDateRangePickerProps,
  DateValue,
  ValidationResult,
} from 'react-aria-components';
import { CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import './DateRangePicker.css';

export interface DateRangePickerProps<T extends DateValue>
  extends AriaDateRangePickerProps<T> {
  label?: string;
  description?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
}

export function DateRangePicker<T extends DateValue>({
  label,
  description,
  errorMessage,
  ...props
}: DateRangePickerProps<T>) {
  return (
    <AriaDateRangePicker {...props} className="kdrp">
      {label && <Label className="kdrp-label">{label}</Label>}
      <Group className="kdrp-field">
        <DateInput slot="start" className="kdrp-input">
          {(segment) => <DateSegment segment={segment} className="kdrp-seg" />}
        </DateInput>
        <span aria-hidden className="kdrp-dash">–</span>
        <DateInput slot="end" className="kdrp-input">
          {(segment) => <DateSegment segment={segment} className="kdrp-seg" />}
        </DateInput>
        <Button className="kdrp-trigger">
          <CalendarIcon size={15} />
        </Button>
      </Group>
      {description && <Text slot="description" className="kdrp-desc">{description}</Text>}
      <FieldError className="kdrp-err">{errorMessage}</FieldError>
      <Popover className="kdrp-pop" offset={4}>
        <Dialog className="kdrp-dlg">
          <RangeCalendar className="kdrp-cal">
            {({ state }) => {
              const mk = `${state.visibleRange.start.year}-${state.visibleRange.start.month}`;
              return (
                <>
                  <header className="kdrp-hdr">
                    <Button slot="previous" className="kdrp-nav">
                      <ChevronLeft size={18} />
                    </Button>
                    <Heading className="kdrp-title" />
                    <Button slot="next" className="kdrp-nav">
                      <ChevronRight size={18} />
                    </Button>
                  </header>
                  <div key={mk} className="kdrp-month-slide">
                    <CalendarGrid className="kdrp-grid">
                      <CalendarGridHeader>
                        {(day) => <CalendarHeaderCell className="kdrp-wday">{day}</CalendarHeaderCell>}
                      </CalendarGridHeader>
                      <CalendarGridBody>
                        {(date) => <CalendarCell date={date} className="kdrp-cell" />}
                      </CalendarGridBody>
                    </CalendarGrid>
                  </div>
                </>
              );
            }}
          </RangeCalendar>
        </Dialog>
      </Popover>
    </AriaDateRangePicker>
  );
}